import { action, computed, observable, autorun } from 'mobx';
import axios from 'axios';

import { ghSearchTerm, ghCustomTollFreeIntent } from '~/common/globals.js';
import { isAreaCodePremium } from '~/common/utils/premiumAreaCodes.js';
import { extractAreaCode } from '~/common/utils/numberOperations.js';
import customTollFreeCharacterRestrictions from '~/common/utils/customTollFreeCharacterRestrictions.js';
import { numberMap } from '~/common/utils/highlightCustomTollFreeNumbers';
import { isBoolean, isArray, isString } from '~/common/utils/typeCheckers';
import { VANITY_SEARCH_GENERIC_ERROR } from '~/common/utils/errorMessaging';

// import other stores
import { numberLockSessionStore } from '~/common/stores/domain/numberLockSession.js';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite';

import { formsStore } from '~/common/stores/ui/forms';

class CustomTollFreeNumbersClass {
    // term that user searched for in their last search
    @observable activeSearchTerm = '';
    // observable saying we're fetching
    @observable fetchingCustomTollFreeNumbers = false;
    // error fetching custom toll free numbers
    @observable fetchedCustomTollFreeNumbersError = false;
    // numbers returned from the API, for selection
    @observable customTollFreeNumbers = [];
    // prefix the user has selected
    @observable selectedCustomTollFreePrefix = '8**';
    // search term the user has entered
    @observable customTollFreeSearchTerm = '';
    // number selected in modal
    @observable numberSelectedInModal = {};
    // term that user searched for and selected a number from
    @observable selectedNumberTerm = '';
    // if a user has ever searched
    @observable initialSearch = false;
    @observable preselectedCustomTollFreeIntent = false;

    // used in classic pages flow
    // get a list of custom toll free numbers, set tollFreeNumbers array & sets fetchedTollFreeNumbers
    @action getCustomTollFreeNumbers() {
        // remove previous list of custom toll free numbers
        this.setCustomTollFreeNumbers([]);
        // set active modal search term
        this.setActiveSearchTerm(this.customTollFreeSearchTerm);
        // set flag to say we're fetching numbers
        this.setFetchedCustomTollFreeNumbersError(false);
        this.setFetchingCustomTollFreeNumbers(true);
        // url string
        const url = `/api/NumberCatalog/ghnumber?city=TF&areaCode=${this.selectedCustomTollFreePrefix}&numberLockSessionId=${numberLockSessionStore.NLS}&count=10${this.customTollFreeSearchTerm ? `&vanityPattern=${this.customTollFreeSearchTerm}` : ''}${offerSuiteStore.selectedOfferId ? `&offerId=${offerSuiteStore.selectedOfferId}` : ''}`;
        // get a list of custom toll free numbers with specified type and count
        axios.get(url)
            .then((response) => {
            // set custom toll free numbersnumberLockSessionStore
                this.setCustomTollFreeNumbers(response.data);
                // set initial search
                this.setInitialSearch(true);
            }).catch((err) => {
            // set fetched flag
                this.setFetchedCustomTollFreeNumbersError(true);
                return err;
            }).then(() => {
                this.setFetchingCustomTollFreeNumbers(false);
            });
    }

    // used in number picker view
    @action getVanityCustomNumbers(searchTerm, callback, count = 6) {
        // set flag to say we're fetching numbers
        this.setFetchedCustomTollFreeNumbersError(false);
        this.setFetchingCustomTollFreeNumbers(true);
        // url string
        const url = `/api/NumberCatalog/ghnumber?city=TF&areaCode=${this.selectedCustomTollFreePrefix}&numberLockSessionId=${numberLockSessionStore.NLS}&count=${count}${searchTerm ? `&vanityPattern=${searchTerm}` : ''}${offerSuiteStore.selectedOfferId ? `&offerId=${offerSuiteStore.selectedOfferId}` : ''}`;
        // get a list of custom toll free numbers with specified type and count
        axios.get(url)
            .then((response) => {
                // set custom toll free numbersnumberLockSessionStore
                this.setCustomTollFreeNumbers(response.data);
                if (callback && this.customTollFreeNumbers && this.customTollFreeNumbers.length > 0) callback(this.customTollFreeNumbers[0]);
            }).catch((err) => {
                // set fetched flag
                this.setFetchedCustomTollFreeNumbersError(true);
                formsStore.setFieldError('VanitySearchTerm', VANITY_SEARCH_GENERIC_ERROR);
                return err;
            }).then(() => {
                this.setFetchingCustomTollFreeNumbers(false);
            });
    }

    bootstrapStore() {
        autorun((reaction) => {
            // if there is a number lock session or the number lock session changes and search term is passed
            if (numberLockSessionStore.NLS && ghSearchTerm()) {
                // set query string params
                this.setDataFromQueryString();
                reaction.dispose();
            }
        });

        autorun((reaction) => {
            // if user came from toll free page but did not pick a search term
            if (ghCustomTollFreeIntent() && ghCustomTollFreeIntent().toLowerCase() === 'true') {
                this.setPreselectedCustomTollFreeIntent(true);
                reaction.dispose();
            }
        });
    }

    @action setDataFromQueryString() {
        const term = ghSearchTerm();

        // if there is a search term
        if (typeof term === 'string') {
            // set it in the store
            this.setPreselectedTerm(term);
            this.setPreselectedCustomTollFreeIntent(true);
        }
    }

    // set preselected term from query string
    @action setPreselectedTerm(term) {
        // force to uppercase (used for matching)
        const uppercaseTerm = term.toUpperCase();
        // strip out any disallowed characters
        const approvedTerm = customTollFreeCharacterRestrictions(uppercaseTerm);
        // trim term to a max length to 7
        const trimmedString = approvedTerm.substring(0, 7);
        // if seach term exists after trimming and character restrictions
        if (trimmedString.length > 0) {
            // set search term
            this.setActiveSearchTerm(trimmedString);
            this.setSelectedCustomTollFreeSearchTerm(trimmedString);
            this.setSelectedNumberTerm(trimmedString);
            this.getCustomTollFreeNumbers();
        }
    }

    @action resetSearchTerm() {
        this.customTollFreeSearchTerm = '';
    }

    @action setPreselectedCustomTollFreeIntent(bool) {
        // the user came from the custom toll free page
        this.preselectedCustomTollFreeIntent = isBoolean(bool, this.preselectedCustomTollFreeIntent);
    }

    @action setActiveSearchTerm(term) {
        // set custom toll free term user searched in modal
        this.activeSearchTerm = isString(term, this.activeSearchTerm);
    }

    @action setSelectedNumberTerm(term) {
        // set custom toll free term user searched in modal
        this.selectedNumberTerm = isString(term, this.selectedNumberTerm);
    }

    // set selected custom toll free prefix
    @action setSelectedCustomTollFreeSearchTerm(term) {
        this.customTollFreeSearchTerm = isString(term, this.customTollFreeSearchTerm);
    }

    // set custom toll free numbers
    @action setCustomTollFreeNumbers(data) {
        this.customTollFreeNumbers = isArray(data, this.customTollFreeNumbers);
    }

    // set custom toll free numbers error
    @action setFetchedCustomTollFreeNumbersError(bool) {
        this.fetchedCustomTollFreeNumbersError = isBoolean(bool, this.fetchedCustomTollFreeNumbersError);
    }

    @action setFetchingCustomTollFreeNumbers(bool) {
        // set fetching flag
        this.fetchingCustomTollFreeNumbers = isBoolean(bool, this.fetchingCustomTollFreeNumbers);
    }

    @action setInitialSearch(bool) {
        // set initial search flag
        this.initialSearch = isBoolean(bool, this.initialSearch);
    }

    // finds the first custom toll free number that isn't premium (i.e. not an 800 area code)
    @computed get trialValidNumber() {
        if (!Array.isArray(this.customTollFreeNumbers.slice())) return null;

        return this.customTollFreeNumbers.find((num) => {
            const areaCode = extractAreaCode(num);
            if (!areaCode) return false;
            return !isAreaCodePremium(areaCode);
        }) || null;
    }

    @computed get recommendedVanityNumber() {
        // set recommended vanity number if custom toll free numbers
        if (this.customTollFreeNumbers.length && this.activeSearchTerm) {
            // for term upper case for matching
            const term = this.activeSearchTerm.toUpperCase();
            // return full term match
            return this.customTollFreeNumbers.find(num => (
                typeof num.UI === 'string' &&
                num.UI.replace(/[^A-Za-z0-9]/g, '').toUpperCase().includes(term)
            ));
        }
        return false;
    }

    // get numeric representation of vanity search term
    @computed get vanityTermToNumbers() {
        if (this.activeSearchTerm) {
            // split the string into array, and replace numbers
            return this.activeSearchTerm.split('').map((value) => {
                const val = value.toUpperCase();
                // if letter
                if (numberMap[val]) {
                    // return number
                    return numberMap[val];
                }
                // otherwise return value
                return val;
            // join back into a string
            }).join('');
        }
        return false;
    }

    @computed get fetchedCustomTollFreeNumbers() {
        return this.customTollFreeNumbers && this.customTollFreeNumbers.length > 0;
    }

    @computed get firstCustomTollFreeNumber() {
        return this.customTollFreeNumbers && this.customTollFreeNumbers.length > 0 && this.customTollFreeNumbers[0];
    }
}

const customTollFreeNumbersStore = new CustomTollFreeNumbersClass();
export { customTollFreeNumbersStore, CustomTollFreeNumbersClass };
